<template>
  <section class="card">
    <h2>供需审核</h2>
    <p v-if="errorMsg" class="error">{{ errorMsg }}</p>

    <div class="filters filters-panel">
      <div class="flt">
        <input
          v-model.trim="filters.keyword"
          class="flt-input flt-input--wide"
          placeholder="标题 / 联系人 / 电话 / 单位"
          aria-label="标题、联系人、电话、单位"
        />
      </div>
      <div class="flt">
        <FancySelect v-model="filters.postType" class="flt-select" :options="postTypeOptions" placeholder="类型" />
      </div>
      <div class="flt">
        <FancySelect v-model="filters.reviewStatus" class="flt-select" :options="reviewStatusOptions" placeholder="审核状态" />
      </div>
      <div class="flt">
        <FancySelect v-model="filters.statusCode" class="flt-select" :options="statusOptions" placeholder="展示状态" />
      </div>
      <div class="flt">
        <FancySelect v-model="filters.revisionStatus" class="flt-select" :options="revisionStatusOptions" placeholder="变更状态" />
      </div>
      <div class="filter-actions">
        <button type="button" class="btn btn-cancel filter-action-btn" @click="resetFilters">重置</button>
      </div>
    </div>

    <table>
      <thead>
        <tr>
          <th class="col-seq">序号</th>
          <th>类型</th>
          <th class="col-center">标题</th>
          <th>联系人</th>
          <th>联系电话</th>
          <th>审核状态</th>
          <th>变更状态</th>
          <th>展示状态</th>
          <th>操作</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(item, idx) in pagedFilteredRows" :key="item.id">
          <td class="col-seq">{{ rowOffset + idx + 1 }}</td>
          <td>{{ postTypeText(item.postTypeCode) }}</td>
          <td class="col-center cell-title" :title="item.title">{{ item.title }}</td>
          <td>{{ item.contactName || '—' }}</td>
          <td>{{ item.contactPhone || '—' }}</td>
          <td>
            <span class="pill" :class="reviewPillClass(item.reviewStatusCode)">
              {{ reviewText(item.reviewStatusCode) }}
            </span>
          </td>
          <td>
            <span class="pill" :class="revisionPillClass(revisionCodeOf(item))">
              {{ revisionStatusText(revisionCodeOf(item)) }}
            </span>
          </td>
          <td>
            <StatusSwitch
              :model-value="displaySwitchOn(item)"
              :disabled="isDisplaySwitchDisabled(item)"
              :title="displaySwitchTitle(item)"
              @change="(v) => setDisplayStatus(item, v)"
            />
          </td>
          <td class="col-ops">
            <button type="button" class="btn btn-edit" @click="openDetail(item)">查看详情</button>
            <button
              v-if="canRevisionReview(item)"
              type="button"
              class="btn btn-edit"
              @click="openRevisionDetail(item)"
            >查看变更详情</button>
          </td>
        </tr>
      </tbody>
    </table>
    <ListPager
      v-model:page="page"
      v-model:page-size="pageSize"
      :total="filteredRows.length"
    />
    <p v-if="!filteredRows.length && !errorMsg" class="muted">暂无符合条件的供需信息</p>

    <div v-if="showDetail" class="mask" @click.self="showDetail = false">
      <div class="dialog dialog--shell dialog--detail">
        <div class="dialog-head">
          <h3 class="dialog-title">供需详情</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="showDetail = false">×</button>
        </div>
        <div class="dialog-body">
<div v-if="detailItem" class="detail-grid">
          <p v-if="canRevisionReview(detailItem)" class="detail-hint">
            该帖有变更待审，此处为<strong>当前线上版本</strong>；待审内容请点「查看变更详情」。
          </p>
          <div class="detail-row"><span class="detail-k">类型</span><span class="detail-v">{{ postTypeText(detailItem.postTypeCode) }}</span></div>
          <div class="detail-row"><span class="detail-k">标题</span><span class="detail-v">{{ detailItem.title || '—' }}</span></div>
          <div class="detail-row"><span class="detail-k">所属行业</span><span class="detail-v">{{ detailItem.category || '—' }}</span></div>
          <div class="detail-row"><span class="detail-k">合作类型</span><span class="detail-v">{{ detailExtra.coopTypeText }}</span></div>
          <div class="detail-row"><span class="detail-k">合作期限</span><span class="detail-v">{{ detailExtra.periodText }}</span></div>
          <div class="detail-row"><span class="detail-k">合作地区</span><span class="detail-v">{{ detailExtra.regionText }}</span></div>
          <div class="detail-row"><span class="detail-k">单位</span><span class="detail-v">{{ detailItem.companyName || '—' }}</span></div>
          <div class="detail-row"><span class="detail-k">联系人</span><span class="detail-v">{{ detailItem.contactName || '—' }}</span></div>
          <div class="detail-row"><span class="detail-k">联系电话</span><span class="detail-v">{{ detailItem.contactPhone || '—' }}</span></div>
          <div class="detail-row"><span class="detail-k">审核状态</span><span class="detail-v">
            <span class="pill" :class="reviewPillClass(detailItem.reviewStatusCode)">
              {{ reviewText(detailItem.reviewStatusCode) }}
            </span>
          </span></div>
          <div class="detail-row"><span class="detail-k">变更状态</span><span class="detail-v">
            <span class="pill" :class="revisionPillClass(revisionCodeOf(detailItem))">
              {{ revisionStatusText(revisionCodeOf(detailItem)) }}
            </span>
          </span></div>
          <div class="detail-row detail-row--switch"><span class="detail-k">展示状态</span><span class="detail-v">
            <StatusSwitch
              :model-value="displaySwitchOn(detailItem)"
              :disabled="isDisplaySwitchDisabled(detailItem)"
              :title="displaySwitchTitle(detailItem)"
              @change="(v) => setDisplayStatus(detailItem, v)"
            />
            <span class="detail-switch-hint">{{ statusText(detailItem.statusCode) }}</span>
          </span></div>
          <div class="detail-row"><span class="detail-k">提交时间</span><span class="detail-v">{{ formatDateTime(detailItem.createdAt) }}</span></div>
          <div v-if="detailReward" class="detail-row detail-row--block">
            <span class="detail-k">撮合激励</span>
            <div class="detail-reward">
              <p v-for="(line, i) in detailReward.lines" :key="i">{{ line }}</p>
              <p class="detail-reward-meta">
                对外展示：{{ detailReward.publicVisible ? '是' : '否（仅秘书处可见）' }}
              </p>
            </div>
          </div>
          <div class="detail-row detail-row--block">
            <span class="detail-k">正文</span>
            <div class="detail-content" v-html="detailItem.content || '—'" />
          </div>
          <div v-if="detailAttachments.length" class="detail-row detail-row--block">
            <span class="detail-k">附件</span>
            <ul class="detail-att-list">
              <li v-for="(att, i) in detailAttachments" :key="att.id || i">
                <a class="detail-att-link" :href="attachmentHref(att)" target="_blank" rel="noopener noreferrer">
                  {{ att.name }}
                </a>
              </li>
            </ul>
          </div>
        </div>
        </div>
        <div class="dialog-foot dialog-foot--split">
          <button type="button" class="btn btn-cancel" @click="showDetail = false">关闭</button>
          <div v-if="canFirstReview(detailItem) && canReview" class="dialog-foot-actions">
            <button type="button" class="btn btn-delete" @click="openReview(detailItem, 'REJECT')">驳回</button>
            <button type="button" class="btn btn-primary" @click="openReview(detailItem, 'APPROVE')">通过</button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showRevisionDetail" class="mask" @click.self="showRevisionDetail = false">
      <div class="dialog dialog--shell dialog--detail">
        <div class="dialog-head">
          <h3 class="dialog-title">变更详情（待审版本）</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="showRevisionDetail = false">×</button>
        </div>
        <div class="dialog-body">
          <div v-if="revisionView" class="detail-grid">
            <p class="detail-hint detail-hint--revision">
              以下为会员提交的变更草稿；通过后才会替换线上展示内容。
            </p>
            <div class="detail-row"><span class="detail-k">类型</span><span class="detail-v">{{ postTypeText(revisionView.postTypeCode) }}</span></div>
            <div class="detail-row"><span class="detail-k">标题</span><span class="detail-v">{{ revisionView.title || '—' }}</span></div>
            <div class="detail-row"><span class="detail-k">所属行业</span><span class="detail-v">{{ revisionView.category || '—' }}</span></div>
            <div class="detail-row"><span class="detail-k">合作类型</span><span class="detail-v">{{ revisionView.extra.coopTypeText }}</span></div>
            <div class="detail-row"><span class="detail-k">合作期限</span><span class="detail-v">{{ revisionView.extra.periodText }}</span></div>
            <div class="detail-row"><span class="detail-k">合作地区</span><span class="detail-v">{{ revisionView.extra.regionText }}</span></div>
            <div class="detail-row"><span class="detail-k">单位</span><span class="detail-v">{{ revisionView.companyName || '—' }}</span></div>
            <div class="detail-row"><span class="detail-k">联系人</span><span class="detail-v">{{ revisionView.contactName || '—' }}</span></div>
            <div class="detail-row"><span class="detail-k">联系电话</span><span class="detail-v">{{ revisionView.contactPhone || '—' }}</span></div>
            <div class="detail-row"><span class="detail-k">提交变更</span><span class="detail-v">{{ formatDateTime(revisionView.submittedAt) }}</span></div>
            <div v-if="revisionView.reward" class="detail-row detail-row--block">
              <span class="detail-k">撮合激励</span>
              <div class="detail-reward">
                <p v-for="(line, i) in revisionView.reward.lines" :key="i">{{ line }}</p>
                <p class="detail-reward-meta">
                  对外展示：{{ revisionView.reward.publicVisible ? '是' : '否（仅秘书处可见）' }}
                </p>
              </div>
            </div>
            <div class="detail-row detail-row--block">
              <span class="detail-k">正文</span>
              <div class="detail-content" v-html="revisionView.content || '—'" />
            </div>
            <div v-if="revisionView.attachments.length" class="detail-row detail-row--block">
              <span class="detail-k">附件</span>
              <ul class="detail-att-list">
                <li v-for="(att, i) in revisionView.attachments" :key="att.id || i">
                  <a class="detail-att-link" :href="attachmentHref(att)" target="_blank" rel="noopener noreferrer">
                    {{ att.name }}
                  </a>
                </li>
              </ul>
            </div>
            <label v-if="canReview" class="field field--revision">
              <span>驳回原因（驳回变更时填写，可空）</span>
              <textarea v-model="revisionReason" placeholder="可空" />
            </label>
          </div>
        </div>
        <div class="dialog-foot dialog-foot--split">
          <button type="button" class="btn btn-cancel" @click="showRevisionDetail = false">关闭</button>
          <div v-if="canReview && revisionDetailItem" class="dialog-foot-actions">
            <button type="button" class="btn btn-delete" @click="reviewRevision('REJECT')">驳回变更</button>
            <button type="button" class="btn btn-primary" @click="reviewRevision('APPROVE')">通过变更</button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showDialog" class="mask">
      <div class="dialog dialog--shell">
        <div class="dialog-head">
          <h3 class="dialog-title">{{ actionCode === 'APPROVE' ? '审核通过' : '审核驳回' }}：{{ current?.title }}</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="showDialog = false">×</button>
        </div>
        <div class="dialog-body">
<div class="review-tag" :class="actionCode === 'APPROVE' ? 'ok' : 'reject'">
          {{ actionCode === 'APPROVE' ? '通过后将自动上架展示' : '驳回后前端不可见' }}
        </div>
        <label class="field">
          <span>审核人：</span>
          <input v-model="reviewer" readonly>
          <small class="hint">取当前登录后台用户（本地标识）。</small>
        </label>
        <label class="field"><span>原因：</span><textarea v-model="reason" placeholder="可空"/></label>
        </div>

        <div class="dialog-foot">
          <button type="button" class="btn btn-cancel" @click="showDialog = false">取消</button>
          <button type="button" class="btn btn-primary" @click="submitReview">确认</button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { getSupplyPostsForReview, reviewSupplyPost, reviewSupplyRevision, updateSupplyPostStatus } from '@/api'
import FancySelect from '@/components/FancySelect.vue'
import ListPager from '@/components/ListPager.vue'
import StatusSwitch from '@/components/StatusSwitch.vue'
import { clampPage, pageRowOffset, slicePageRows } from '@/utils/listPager'
import { hasAnyPermission } from '@/utils/adminPermission'
import {
  supplyExtraDisplay,
  supplyAttachments,
  attachmentHref,
  supplyRewardDisplay,
  supplyRevisionStatusCode,
  revisionStatusText,
  revisionPillClass,
  canFirstReview,
  canRevisionReview,
  buildRevisionDetailView,
} from '@/utils/supplyExtra'

const EMPTY_FILTERS = () => ({
  keyword: '',
  postType: '',
  reviewStatus: '',
  statusCode: '',
  revisionStatus: '',
})

export default {
  name: 'SupplyReviewView',
  components: { FancySelect, ListPager, StatusSwitch },
  data() {
    return {
      list: [],
      filters: EMPTY_FILTERS(),
      page: 1,
      pageSize: 10,
      errorMsg: '',
      showDialog: false,
      showDetail: false,
      showRevisionDetail: false,
      detailItem: null,
      revisionDetailItem: null,
      revisionReason: '',
      current: null,
      actionCode: 'APPROVE',
      reviewer: '',
      reason: '',
      canReview: false,
      postTypeOptions: [
        { value: '', label: '全部' },
        { value: 'SUPPLY', label: '供应' },
        { value: 'DEMAND', label: '需求' },
      ],
      reviewStatusOptions: [
        { value: '', label: '全部' },
        { value: 'PENDING', label: '待审核' },
        { value: 'APPROVED', label: '已通过' },
        { value: 'REJECTED', label: '已驳回' },
      ],
      statusOptions: [
        { value: '', label: '全部' },
        { value: 'ENABLED', label: '启用' },
        { value: 'DISABLED', label: '停用' },
      ],
      revisionStatusOptions: [
        { value: '', label: '全部' },
        { value: 'NONE', label: '无变更' },
        { value: 'PENDING', label: '变更待审' },
        { value: 'REJECTED', label: '变更已驳回' },
      ],
    }
  },
  computed: {
    /** 按 id 升序排列，序号列从 1 递增 */
    filteredRows() {
      const f = this.filters || EMPTY_FILTERS()
      const kw = String(f.keyword || '').trim().toLowerCase()
      return (this.list || [])
        .filter((item) => {
          if (f.postType && String(item.postTypeCode || '') !== f.postType) return false
          if (f.reviewStatus && String(item.reviewStatusCode || '') !== f.reviewStatus) return false
          if (f.statusCode && String(item.statusCode || '') !== f.statusCode) return false
          const rev = supplyRevisionStatusCode(item)
          if (f.revisionStatus === 'NONE' && rev) return false
          if (f.revisionStatus && f.revisionStatus !== 'NONE' && rev !== f.revisionStatus) return false
          if (!kw) return true
          const hay = [
            item.title,
            item.contactName,
            item.contactPhone,
            item.companyName,
            item.category,
          ]
            .map((x) => String(x || '').toLowerCase())
            .join(' ')
          return hay.includes(kw)
        })
        .slice()
        .sort((a, b) => Number(a.id || 0) - Number(b.id || 0))
    },
    rowOffset() {
      return pageRowOffset(this.page, this.pageSize)
    },
    pagedFilteredRows() {
      return slicePageRows(this.filteredRows, this.page, this.pageSize)
    },
    detailExtra() {
      return supplyExtraDisplay(this.detailItem)
    },
    detailAttachments() {
      return supplyAttachments(this.detailItem)
    },
    detailReward() {
      return supplyRewardDisplay(this.detailItem)
    },
    revisionView() {
      return buildRevisionDetailView(this.revisionDetailItem)
    },
  },
  watch: {
    'filteredRows.length'(n) {
      this.page = clampPage(this.page, n, this.pageSize)
    },
  },
  mounted() {
    this.canReview = hasAnyPermission(['supply.review'])
    this.applyRouteFilters()
    this.load().then(() => this.applyRouteFocus())
  },
  methods: {
    async load() {
      try {
        this.list = (await getSupplyPostsForReview()) || []
      } catch (e) {
        this.errorMsg = e.message || '读取失败'
      }
    },
    resetFilters() {
      this.page = 1
      this.filters = EMPTY_FILTERS()
    },
    applyRouteFilters() {
      const q = (this.$route && this.$route.query) || {}
      const rs = String(q.revisionStatus || '').trim().toUpperCase()
      if (rs === 'PENDING' || rs === 'REJECTED' || rs === 'NONE') {
        this.filters.revisionStatus = rs
      }
      const review = String(q.reviewStatus || '').trim().toUpperCase()
      if (review) {
        this.filters.reviewStatus = review
      }
    },
    applyRouteFocus() {
      const q = (this.$route && this.$route.query) || {}
      const raw = q.focusPostId != null ? q.focusPostId : q.focusId
      const focusId = Number(raw)
      if (!Number.isFinite(focusId) || focusId <= 0) return
      const item = (this.list || []).find((x) => Number(x.id) === focusId)
      if (!item) return
      if (canRevisionReview(item)) {
        this.openRevisionDetail(item)
        return
      }
      this.openDetail(item)
    },
    openDetail(item) {
      this.detailItem = item || null
      this.showDetail = !!item
    },
    openRevisionDetail(item) {
      this.revisionDetailItem = item || null
      this.revisionReason = ''
      this.showRevisionDetail = !!(item && canRevisionReview(item))
    },
    openReview(item, actionCode) {
      if (!this.canReview) {
        this.errorMsg = '权限不足：需要 supply.review'
        return
      }
      this.current = item
      this.actionCode = actionCode
      this.reviewer = localStorage.getItem('admin.username') || 'admin'
      this.reason = ''
      this.showDialog = true
      this.showDetail = false
    },
    postTypeText(code) {
      return code === 'SUPPLY' ? '供应' : code === 'DEMAND' ? '需求' : code || '—'
    },
    reviewText(code) {
      const map = { PENDING: '待审核', APPROVED: '已通过', REJECTED: '已驳回' }
      return map[code] || code || '—'
    },
    reviewPillClass(code) {
      const c = String(code || '').toUpperCase()
      if (c === 'APPROVED') return 'pill-green'
      if (c === 'REJECTED') return 'pill-red'
      if (c === 'PENDING') return 'pill-warn'
      return 'pill-gray'
    },
    statusText(code) {
      return code === 'ENABLED' ? '启用' : code === 'DISABLED' ? '停用' : code || '—'
    },
    displaySwitchOn(item) {
      if (!item) return false
      if (String(item.reviewStatusCode || '').toUpperCase() !== 'APPROVED') return false
      return String(item.statusCode || '').toUpperCase() === 'ENABLED'
    },
    isDisplaySwitchDisabled(item) {
      if (!item) return true
      if (!this.canReview) return true
      return String(item.reviewStatusCode || '').toUpperCase() !== 'APPROVED'
    },
    displaySwitchTitle(item) {
      if (!this.canReview) return '需要 supply.review 权限'
      const review = String(item?.reviewStatusCode || '').toUpperCase()
      if (review === 'PENDING') return '待审核，暂不可调整展示状态'
      if (review === 'REJECTED') return '已驳回，不可上架展示'
      return item?.statusCode === 'ENABLED' ? '点击停用' : '点击启用'
    },
    async setDisplayStatus(item, enabled) {
      if (!item || item.id == null) return
      if (this.isDisplaySwitchDisabled(item)) return
      const next = enabled ? 'ENABLED' : 'DISABLED'
      const prev = item.statusCode
      item.statusCode = next
      try {
        await updateSupplyPostStatus(item.id, next)
        this.errorMsg = ''
      } catch (e) {
        item.statusCode = prev
        this.errorMsg = e.message || '切换展示状态失败'
      }
    },
    formatDateTime(v) {
      if (!v) return '—'
      const s = String(v).replace('T', ' ')
      return s.length >= 19 ? s.slice(0, 19) : s
    },
    attachmentHref,
    revisionCodeOf: supplyRevisionStatusCode,
    revisionStatusText,
    revisionPillClass,
    canFirstReview,
    canRevisionReview,
    async reviewRevision(actionCode) {
      const item = this.revisionDetailItem
      if (!item || item.id == null) return
      if (!this.canReview) {
        this.errorMsg = '权限不足：需要 supply.review'
        return
      }
      const label = actionCode === 'APPROVE' ? '通过变更' : '驳回变更'
      try {
        await reviewSupplyRevision(item.id, {
          actionCode,
          reviewer: localStorage.getItem('admin.username') || 'admin',
          reason: actionCode === 'REJECT' ? this.revisionReason : '',
        })
        this.showRevisionDetail = false
        this.revisionDetailItem = null
        this.revisionReason = ''
        this.errorMsg = ''
        await this.load()
      } catch (e) {
        this.errorMsg = e.message || `${label}失败`
      }
    },
    async submitReview() {
      if (!this.current) return
      try {
        await reviewSupplyPost(this.current.id, {
          actionCode: this.actionCode,
          reviewer: this.reviewer,
          reason: this.reason,
        })
        this.showDialog = false
        await this.load()
      } catch (e) {
        this.errorMsg = e.message || '审核失败'
      }
    },
  },
}
</script>

<style scoped>
.card { background: #fff; border-radius: 12px; padding: 18px; }
.filters .flt-input {
  width: 180px;
  height: 40px;
  box-sizing: border-box;
  border: 1px solid #d0d7e2;
  border-radius: 8px;
  padding: 0 10px;
  font-size: 13px;
  background: #fff;
}
.filters .flt-input--wide { width: 280px; }
.filters .flt-select { width: 170px; }
:deep(.filters .fs-btn) {
  height: 40px;
  min-width: 170px;
  border-radius: 8px;
}
table { width: 100%; border-collapse: collapse; margin-top: 8px; }
th, td {
  border-bottom: 1px solid #edf0f5;
  padding: 10px 8px;
  font-size: 13px;
  text-align: center;
  vertical-align: middle;
}
.col-seq { width: 56px; color: #667085; }
.cell-title {
  max-width: 260px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.col-ops { min-width: 200px; white-space: nowrap; }
.btn { border-radius: 8px; padding: 6px 12px; cursor: pointer; border: 1px solid transparent; font-size: 12px; }
.btn + .btn { margin-left: 8px; }
.btn:disabled { opacity: 0.45; cursor: not-allowed; }
.btn-primary { background: #2f6af6; color: #fff; }
.btn-edit { border-color: #bfc9db; background: #fff; color: #26457d; }
.btn-delete { border-color: #ffc8c2; background: #fff1f0; color: #d92d20; }
.btn-cancel { border-color: #d0d7e2; background: #fff; color: #344054; }
.pill {
  display: inline-block;
  min-width: 58px;
  text-align: center;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 12px;
  line-height: 18px;
  font-weight: 600;
}
.pill-green {
  color: #067647;
  background: #ecfdf3;
  border: 1px solid #abefc6;
}
.pill-red {
  color: #b42318;
  background: #fef3f2;
  border: 1px solid #fecdca;
}
.pill-warn {
  color: #b54708;
  background: #fff6ed;
  border: 1px solid #fedf89;
}
.pill-gray {
  color: #475467;
  background: #f2f4f7;
  border: 1px solid #eaecf0;
}
.pill-purple {
  color: #5925dc;
  background: #f4f3ff;
  border: 1px solid #d9d6fe;
}
.detail-row--switch .detail-v {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.detail-switch-hint {
  font-size: 12px;
  color: #667085;
}
.error { color: #d92d20; }
.muted { color: #98a2b3; font-size: 13px; margin-top: 12px; text-align: center; }
.dialog { width: 560px; max-height: 90vh; overflow: auto; background: #fff; border-radius: 14px; padding: 18px; box-shadow: 0 16px 48px rgba(15, 23, 42, 0.18); }
.dialog--detail { width: 720px; }
.dialog h3 { margin: 0 0 8px; font-size: 18px; color: #1d2939; text-align: center; }
.dialog * { box-sizing: border-box; }
.detail-grid { margin-top: 12px; }
.detail-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #f0f2f5;
  font-size: 13px;
}
.detail-row--block { flex-direction: column; align-items: stretch; }
.detail-row--block .detail-k {
  width: auto;
  text-align: left;
  font-weight: 600;
  color: #344054;
  margin-bottom: 6px;
}
.detail-k { width: 88px; flex-shrink: 0; color: #667085; text-align: right; }
.detail-v { flex: 1; color: #1d2939; word-break: break-all; text-align: left; }
.detail-reward {
  padding: 12px;
  border: 1px solid #edf0f5;
  border-radius: 8px;
  background: #fafbfc;
  text-align: left;
}
.detail-content {
  margin-top: 6px;
  padding: 12px;
  border: 1px solid #edf0f5;
  border-radius: 8px;
  background: #fafbfc;
  text-align: left;
  line-height: 1.6;
  max-height: 360px;
  overflow: auto;
}
.detail-content :deep(img) { max-width: 100%; height: auto; }
.detail-att-list {
  margin: 6px 0 0;
  padding: 12px;
  border: 1px solid #edf0f5;
  border-radius: 8px;
  background: #fafbfc;
  list-style: none;
}
.detail-att-list li + li { margin-top: 8px; }
.detail-att-link {
  color: #2f6af6;
  text-decoration: none;
  word-break: break-all;
}
.detail-att-link:hover { text-decoration: underline; }
.detail-hint {
  margin: 0 0 12px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fff6ed;
  color: #b54708;
  font-size: 12px;
  line-height: 1.55;
  text-align: left;
}
.detail-hint--revision {
  background: #f4f3ff;
  color: #5925dc;
}
.dialog-foot--split {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.dialog-foot-actions {
  display: flex;
  gap: 8px;
}
.field--revision {
  margin-top: 4px;
}
.detail-reward p,
.detail-revision p {
  margin: 0 0 8px;
  font-size: 13px;
  line-height: 1.55;
  text-align: left;
}
.detail-reward p:last-child,
.detail-revision p:last-child {
  margin-bottom: 0;
}
.detail-reward-meta {
  color: #667085;
  font-size: 12px;
}
.detail-revision-actions {
  margin-top: 10px;
}
.review-tag { margin-top: 2px; border-radius: 999px; padding: 4px 10px; font-size: 12px; font-weight: 600; display: inline-block; }
.review-tag.ok { background: #ecfdf3; color: #027a48; border: 1px solid #a6f4c5; }
.review-tag.reject { background: #fff1f3; color: #c01048; border: 1px solid #fecdd6; }
.field { display: flex; flex-direction: column; gap: 6px; margin-top: 10px; font-size: 13px; color: #344054; }
.field > span { font-weight: 600; }
input, textarea { width: 100%; border: 1px solid #d0d7e2; border-radius: 10px; padding: 9px 12px; background: #fcfdff; }
textarea { min-height: 110px; resize: vertical; }
.hint { color: #98a2b3; font-size: 12px; margin-top: 2px; }
</style>
